import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { buildTitle, DEFAULT_OG_IMAGE, SITE_URL } from '../utils/seo';
import localProducts from '../data/products.json';

// Normalise products.json shape → ProductCard shape
function normalise(p) {
  return {
    id: p.id,
    title: p.title,
    excerpt: p.description || p.excerpt || '',
    image: p.image || null,
    price: p.price != null ? String(p.price) : '0',
    affiliateLink: p.affiliateLink || p.tiktokAffiliateLink || p.amazonAffiliateLink || '#',
    category: (p.tags && p.tags[0]) || 'general',
    rating: 0,
    onSale: false,
    isFree: p.isFree || false,
    ctaLabel: p.ctaLabel || null,
  };
}

const ALL_PRODUCTS = localProducts.map(normalise);

export default function Products() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  const categories = useMemo(() => {
    const seen = new Set();
    ALL_PRODUCTS.forEach(p => { if (p.category) seen.add(p.category); });
    return ['All', ...Array.from(seen)];
  }, []);

  const filteredProducts = useMemo(() => {
    return ALL_PRODUCTS.filter(p => {
      const matchesCategory = activeCategory === 'All' || p.category === activeCategory;
      const matchesSearch = !search.trim() ||
        p.title.toLowerCase().includes(search.toLowerCase()) ||
        p.excerpt.toLowerCase().includes(search.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, search]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": buildTitle('Resource Store'),
    "description": "Curated tools and product recommendations that support daily life and integration in Germany.",
    "url": `${SITE_URL}/store`,
  };

  return (
    <>
      <Helmet>
        <title>{buildTitle('Resource Store')}</title>
        <meta name="description" content="Curated tools and product recommendations from Sista Kyerewaa to support integration, wellness, and daily life in Germany." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/store`} />
        <meta property="og:title" content={buildTitle('Resource Store')} />
        <meta property="og:description" content="Curated tools and product recommendations from Sista Kyerewaa to support integration, wellness, and daily life in Germany." />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${SITE_URL}/store`} />
        <meta property="twitter:title" content={buildTitle('Resource Store')} />
        <meta property="twitter:description" content="Curated tools and product recommendations from Sista Kyerewaa to support integration, wellness, and daily life in Germany." />
        <meta property="twitter:image" content={DEFAULT_OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <link rel="canonical" href={`${SITE_URL}/store`} />
      </Helmet>

      <main className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.35em] text-[#CC5833]">Store</p>
          <h1 className="font-display text-4xl md:text-5xl mt-4 text-[#1c1b1a]">Curated resources</h1>
          <p className="text-[#6a6661] mt-4 max-w-2xl mx-auto">
            Hand-picked tools, books, and services to support your integration journey and daily life in Germany.
          </p>
        </div>

        {/* TikTok affiliate coming-soon banner */}
        <div className="border border-[#8C7B6B]/30 bg-[#F2F0E9] p-6 mb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#CC5833]">Coming soon</p>
            <p className="font-display text-xl text-[#2E4036] mt-1">TikTok Shop affiliate products</p>
            <p className="text-sm text-[#6a6661] mt-1">Sista Kyerewaa's personal product picks from TikTok Shop — landing here soon.</p>
          </div>
          <a
            href="https://www.tiktok.com/@sistakyerewaa"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-[#2E4036] text-[#F2F0E9] text-xs uppercase tracking-[0.25em] hover:bg-[#1a2a20] transition-colors whitespace-nowrap"
          >
            See picks on TikTok
          </a>
        </div>

        {/* Filters + search */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors min-h-[44px] ${
                  activeCategory === cat
                    ? 'bg-[#2d2b29] text-[#f5f4f2] border-[#2d2b29]'
                    : 'bg-transparent text-[#6b6660] border-stone-200 hover:text-[#2d2b29]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Search resources..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full md:w-72 px-4 py-2 border border-stone-200 bg-white/70 focus:outline-none focus:ring-2 focus:ring-[#c9c5bf]"
          />
        </div>

        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center text-[#6a6661] py-16">
            <p className="text-lg">No resources found. Try a different search or category.</p>
            {(search || activeCategory !== 'All') && (
              <button
                onClick={() => { setSearch(''); setActiveCategory('All'); }}
                className="mt-4 text-sm uppercase tracking-[0.25em] text-[#2d2b29] underline"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        <div className="mt-16 border border-[#8C7B6B]/20 bg-[#2E4036] p-8 text-center">
          <p className="text-xs uppercase tracking-[0.35em] text-[#CC5833]">Free resource</p>
          <h2 className="font-display text-2xl md:text-3xl text-[#F2F0E9] mt-3">Germany Starter Checklist</h2>
          <p className="text-[#F2F0E9]/80 mt-3 max-w-xl mx-auto">
            The free checklist covering your first steps in Germany — Anmeldung, Krankenkasse, and more.
          </p>
          <Link
            to="/checklist"
            className="inline-flex items-center justify-center mt-6 px-8 py-3 border border-[#F2F0E9]/40 text-[#F2F0E9] text-xs uppercase tracking-[0.25em] hover:bg-[#F2F0E9]/10 transition-colors"
          >
            Get the checklist free
          </Link>
        </div>
      </main>
    </>
  );
}
